import { useUserRole } from './useUserRole';

export interface AdminPermissions {
  // System Management
  canManageUsers: boolean;
  canManageRoles: boolean;
  canManageSystem: boolean;
  canViewSystemLogs: boolean;
  canManageConfiguration: boolean;
  
  // Data & Analytics
  canViewAllData: boolean;
  canExportData: boolean;
  canViewAnalytics: boolean;
  canViewSystemMetrics: boolean;
  canAccessReports: boolean;
  
  // Financial Management
  canManageFinances: boolean;
  canViewRevenue: boolean;
  canManageBilling: boolean;
  canViewTransactions: boolean;
  canManagePayments: boolean;
  
  // Security & Compliance
  canManageSecuritySettings: boolean;
  canViewSecurityLogs: boolean;
  canManagePermissions: boolean;
  canAuditAccess: boolean;
  canManageCompliance: boolean;
  
  // Advanced Features
  canImpersonateUsers: boolean;
  canBypassLimits: boolean;
  canManageProviders: boolean;
  canManageIntegrations: boolean;
  canPerformMaintenance: boolean;
  
  // Plan Access
  canAccessAllPlans: boolean;
  canAccessBusinessFeatures: boolean;
  canAccessEnterpriseFeatures: boolean;
  canManageBusinessAccounts: boolean;
  canManageSubscriptions: boolean;
}

export interface AdminAccess {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  permissions: AdminPermissions;
  planAccess: {
    freemium: boolean;
    standard: boolean;
    pro: boolean;
    premium: boolean;
    business: boolean;
    enterprise: boolean;
  };
  limits: {
    dailyTokens: number;
    monthlyTokens: number;
    maxCostPerDay: number;
    canBypassLimits: boolean;
  };
}

export function useAdminPermissions(): AdminAccess {
  const { userRole } = useUserRole();

  if (!userRole || userRole.role !== 'admin') {
    return {
      isAdmin: false,
      isSuperAdmin: false,
      permissions: {
        canManageUsers: false,
        canManageRoles: false,
        canManageSystem: false,
        canViewSystemLogs: false,
        canManageConfiguration: false,
        canViewAllData: false,
        canExportData: false,
        canViewAnalytics: false,
        canViewSystemMetrics: false,
        canAccessReports: false,
        canManageFinances: false,
        canViewRevenue: false,
        canManageBilling: false,
        canViewTransactions: false,
        canManagePayments: false,
        canManageSecuritySettings: false,
        canViewSecurityLogs: false,
        canManagePermissions: false,
        canAuditAccess: false,
        canManageCompliance: false,
        canImpersonateUsers: false,
        canBypassLimits: false,
        canManageProviders: false,
        canManageIntegrations: false,
        canPerformMaintenance: false,
        canAccessAllPlans: false,
        canAccessBusinessFeatures: false,
        canAccessEnterpriseFeatures: false,
        canManageBusinessAccounts: false,
        canManageSubscriptions: false
      },
      planAccess: {
        freemium: false,
        standard: false,
        pro: false,
        premium: false,
        business: false,
        enterprise: false
      },
      limits: {
        dailyTokens: 0,
        monthlyTokens: 0,
        maxCostPerDay: 0,
        canBypassLimits: false
      }
    };
  }

  // Full admin permissions
  return {
    isAdmin: true,
    isSuperAdmin: userRole.userLevel === 'premium', // Super admins have premium user level
    permissions: {
      canManageUsers: true,
      canManageRoles: true,
      canManageSystem: true,
      canViewSystemLogs: true,
      canManageConfiguration: true,
      canViewAllData: true,
      canExportData: true,
      canViewAnalytics: true,
      canViewSystemMetrics: true,
      canAccessReports: true,
      canManageFinances: true,
      canViewRevenue: true,
      canManageBilling: true,
      canViewTransactions: true,
      canManagePayments: true,
      canManageSecuritySettings: true,
      canViewSecurityLogs: true,
      canManagePermissions: true,
      canAuditAccess: true,
      canManageCompliance: true,
      canImpersonateUsers: true,
      canBypassLimits: true,
      canManageProviders: true,
      canManageIntegrations: true,
      canPerformMaintenance: true,
      canAccessAllPlans: true,
      canAccessBusinessFeatures: true,
      canAccessEnterpriseFeatures: true,
      canManageBusinessAccounts: true,
      canManageSubscriptions: true
    },
    planAccess: {
      freemium: true,
      standard: true,
      pro: true,
      premium: true,
      business: true,
      enterprise: true
    },
    limits: {
      dailyTokens: userRole.permissions.dailyTokenLimit,
      monthlyTokens: userRole.permissions.monthlyTokenLimit,
      maxCostPerDay: userRole.permissions.maxCostPerDay,
      canBypassLimits: true
    }
  };
}

// Hook to check specific admin permission
export function useAdminPermission(permission: keyof AdminPermissions) {
  const { permissions } = useAdminPermissions();
  return permissions[permission];
}

// Hook to check plan access
export function useAdminPlanAccess(plan: keyof AdminAccess['planAccess']) {
  const { planAccess } = useAdminPermissions();
  return planAccess[plan];
}